import type { Vec2, AABB, Ring, PolygonWithHoles } from '../geo/types';
import type { SiteModel } from '../site/types';
import type { Obstacle } from '../world/types';
import type { CollisionWorld } from './types';
import { createSpatialHash } from '../geo/spatialHash';
import { createPolygonIndex } from '../geo/polygonIndex';
import { pointInRing, ringBounds } from '../geo/polygon';
import { difference } from '../geo/clip';
import { deckFootprint } from '../site/bridges';
import { boxAround, createSlidingWorld } from './slidingWorld';

const SEGMENT_CELL_M = 16;
const WET_INDEX_CELL_M = 20;
const OBSTACLE_PROBE_M = 0.1;

// PLAN §5: walkable = (land ∪ bridge decks) − obstacles, so only water outside every deck blocks.
function wetRegion(site: SiteModel): readonly PolygonWithHoles[] {
  const decks = [
    ...deckFootprint(site.bridges),
    ...(site.waterAccessDecks ?? []).map((outer) => ({ outer, holes: [] })),
    ...site.damDecks.map((outer) => ({ outer, holes: [] })),
  ];
  return decks.length === 0 ? site.water : difference(site.water, decks);
}

export function boundsRing(bounds: AABB): Ring {
  return [
    [bounds.minX, bounds.minY],
    [bounds.maxX, bounds.minY],
    [bounds.maxX, bounds.maxY],
    [bounds.minX, bounds.maxY],
  ];
}

/** Whether a point lies inside any of the obstacles' rings. */
export function obstacleProbe(obstacles: readonly Obstacle[]): (p: Vec2) => boolean {
  const hash = createSpatialHash<Ring>(SEGMENT_CELL_M);
  for (const { polygon } of obstacles) hash.insert(polygon, ringBounds(polygon));
  return (p) => hash.query(boxAround(p, OBSTACLE_PROBE_M)).some((ring) => pointInRing(p, ring));
}

export function buildCollisionWorld(
  site: SiteModel,
  obstacles: readonly Obstacle[],
  radius: number
): CollisionWorld {
  const { bounds } = site;
  const wet = wetRegion(site);
  const wetIndex = createPolygonIndex(wet, WET_INDEX_CELL_M);
  const inObstacle = obstacleProbe(obstacles);

  return createSlidingWorld({
    radius,
    rings: [
      ...obstacles.map((obstacle) => obstacle.polygon),
      ...wet.flatMap((poly) => [poly.outer, ...poly.holes]),
      boundsRing(bounds),
    ],
    open(p) {
      const inBounds =
        p[0] >= bounds.minX && p[0] <= bounds.maxX && p[1] >= bounds.minY && p[1] <= bounds.maxY;
      return inBounds && !wetIndex.contains(p) && !inObstacle(p);
    },
  });
}
